import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

import {
  latestBackfillJobKey,
  useBackfillJob,
  useCancelBackfillJob,
  useCreateBackfillJob,
  useLatestBackfillJob,
} from "../api/use-backfill-job";
import {
  BACKFILL_WINDOWS,
  ingestedTotal,
  isJobActive,
  type BackfillJob,
  type BackfillMarketplace,
  type BackfillWindow,
} from "../lib/backfill-job";
import { MARKETPLACE_LABEL } from "../lib/marketplace-labels";

const CONFIRM_WINDOWS: ReadonlySet<BackfillWindow> = new Set(["30d", "90d"]);

function mkpLabel(marketplace: string): string {
  return MARKETPLACE_LABEL[marketplace] ?? marketplace;
}

function JobChip({ m }: { m: BackfillMarketplace }): JSX.Element {
  const { t } = useTranslation("orders");
  const mkp = mkpLabel(m.marketplace);
  const testId = `sync-chip-${m.marketplace}`;
  switch (m.status) {
    case "done":
      return (
        <Badge intent="success" data-testid={testId}>
          {t("sync_chip_done", { mkp })}
        </Badge>
      );
    case "failed":
      return (
        <Badge intent="danger" data-testid={testId}>
          {t("sync_chip_failed", { mkp, error: m.error })}
        </Badge>
      );
    case "cancelled":
      return (
        <Badge intent="neutral" data-testid={testId}>
          {t("sync_chip_cancelled", { mkp })}
        </Badge>
      );
    case "running":
      return (
        <Badge intent="info" data-testid={testId}>
          {t("sync_chip_running", { mkp, ingested: m.ingested, fetched: m.fetched })}
        </Badge>
      );
    // ponytail: unrecognized status treated as queued — fail open, not closed.
    default:
      return (
        <Badge intent="neutral" data-testid={testId}>
          {t("sync_chip_queued", { mkp })}
        </Badge>
      );
  }
}

/**
 * "Sync ▾" header action. Picks a window → POST /v1/orders/backfill (30d/90d
 * confirm first) → polls the job with one chip per marketplace and a Cancel
 * action → toasts a summary on the terminal status and invalidates the orders
 * queries. A reload while a job is active resumes from the latest-job query.
 */
export function SyncMenu(): JSX.Element {
  const { t } = useTranslation("orders");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pendingWindow, setPendingWindow] = useState<BackfillWindow | null>(null);
  const [ownJobId, setOwnJobId] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const create = useCreateBackfillJob();
  const cancel = useCancelBackfillJob();
  const { data: latest } = useLatestBackfillJob();
  const jobId = ownJobId ?? (latest && isJobActive(latest.status) ? latest.id : null);
  const { data: job } = useBackfillJob(jobId);
  const syncing = job !== undefined && isJobActive(job.status);

  // Active → terminal transition: toast + invalidate. The ref remembers the
  // previous poll's status across renders.
  const prevStatus = useRef<BackfillJob["status"] | null>(null);
  useEffect(() => {
    if (!job) return;
    const was = prevStatus.current;
    prevStatus.current = job.status;
    if (was === null || !isJobActive(was) || isJobActive(job.status)) return;
    const count = ingestedTotal(job);
    if (job.status === "done") {
      toast.success(t("t_sync_done", { count }));
    } else if (job.status === "cancelled") {
      toast.info(t("t_sync_cancelled", { count }));
    } else {
      toast.warn(t("t_sync_partial", { count }));
      for (const m of job.marketplaces) {
        if (m.status === "failed") {
          toast.error(t("t_sync_mkp_failed", { mkp: mkpLabel(m.marketplace), error: m.error }));
        }
      }
    }
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["order"] });
    void queryClient.invalidateQueries({ queryKey: latestBackfillJobKey });
  }, [job, queryClient, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  function start(window: BackfillWindow): void {
    setOpen(false);
    setPendingWindow(null);
    create.mutate(window, {
      onSuccess: (created) => setOwnJobId(created.id),
      onError: (err) => {
        const status = err instanceof ApiError ? err.status : null;
        toast.error(t(status === 503 ? "t_sync_unavailable" : "t_sync_start_fail"));
      },
    });
  }

  function pick(window: BackfillWindow): void {
    if (CONFIRM_WINDOWS.has(window)) {
      setOpen(false);
      setPendingWindow(window);
      return;
    }
    start(window);
  }

  if (syncing && job) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="sync-progress">
        <span className="text-sm text-[var(--text-subtle)]">{t("sync_syncing")}</span>
        {job.marketplaces.map((m) => (
          <JobChip key={m.marketplace} m={m} />
        ))}
        {/* Cancel is a 202 request, not an instant stop: the job stays queued/
            running until the worker halts between pages. Once cancel_requested
            is set the button must stay out, or a second click 409s. */}
        <Button
          variant="ghost"
          size="sm"
          data-testid="sync-cancel"
          disabled={cancel.isPending || job.cancel_requested}
          onClick={() =>
            cancel.mutate(job.id, { onError: () => toast.error(t("t_sync_cancel_fail")) })
          }
        >
          <X aria-hidden className="h-4 w-4" />
          {t("sync_cancel")}
        </Button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        data-testid="sync-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={create.isPending}
        onClick={() => setOpen((o) => !o)}
      >
        {t("sync_menu")}
        <ChevronDown aria-hidden className="ml-1 h-4 w-4" />
      </Button>
      {open && (
        <div
          role="menu"
          aria-label={t("sync_menu_label")}
          className="absolute right-0 z-[80] mt-1.5 w-44 rounded-xl border border-[var(--premium-border)] bg-white p-1.5 shadow-[var(--sh-pop)]"
        >
          {BACKFILL_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              role="menuitem"
              data-testid={`sync-window-${w}`}
              onClick={() => pick(w)}
              className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm text-[var(--text-primary)] transition hover:bg-[var(--canvas)]"
            >
              {t(`sync_window_${w}`)}
            </button>
          ))}
        </div>
      )}
      {pendingWindow && (
        <ConfirmDialog
          title={t("sync_confirm_title", { window: t(`sync_window_${pendingWindow}`) })}
          description={t("sync_confirm_body")}
          confirmLabel={t("sync_confirm_ok")}
          cancelLabel={t("sync_confirm_cancel")}
          isPending={create.isPending}
          onConfirm={() => start(pendingWindow)}
          onCancel={() => setPendingWindow(null)}
        />
      )}
    </div>
  );
}
